// Phase 84.M.93 — Non-dismissable modal forcing Google-signin users to
// register + verify phone via Zalo ZNS OTP before they can use the app.
//
// Flow:
//   1) User logs in via Google OAuth → backend creates account with
//      `auth_provider="google"` + `is_phone_verified=false`.
//   2) FE `PhoneVerifyGateProvider` (sibling context) detects this state
//      and mounts THIS modal — no close button, no overlay-click dismiss.
//   3) User enters phone → click "Gửi mã OTP" → re-uses PhoneOtpModal flow.
//   4) On OTP success → PATCH /api/auth/me { phone } → refresh user → modal
//      auto-dismisses once `is_phone_verified === true`.
//
// Design: matches the dark/orange Pickleague theme; sticky shield icon,
// short helper copy, large input for SĐT (auto-stripped to digits).
import { useState } from "react";
import { ShieldCheck, ArrowRight, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import PhoneOtpModal from "./PhoneOtpModal";
import { authApi, formatApiErrorDetail } from "../../lib/authApi";
import { useAuth } from "../../contexts/AuthContext";

export default function PhoneVerifyRequiredModal({ open }) {
  const { user, refreshMe, logout } = useAuth();
  const [phone, setPhone] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [saving, setSaving] = useState(false);
  // Phase 85.A.47 — Inline warning khi BE check phone đã dùng tài khoản
  // khác (409 pre-Zalo-ZNS). Clear khi user gõ SĐT khác.
  const [phoneTakenWarning, setPhoneTakenWarning] = useState(null);

  if (!open) return null;

  const phoneDigits = phone.replace(/\D/g, "");
  const phoneValid = phoneDigits.length >= 9 && phoneDigits.length <= 15;

  const onVerified = async (verifiedPhone) => {
    setShowOtp(false);
    setSaving(true);
    try {
      await authApi.updateMe({ phone: verifiedPhone });
      await refreshMe();
      toast.success("Đã xác thực Số điện thoại. Chào mừng bạn đến PickLeague!");
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Không lưu được SĐT. Vui lòng thử lại."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[140] flex items-end sm:items-center justify-center bg-black/85 backdrop-blur-md"
      data-testid="phone-verify-required-modal"
    >
      <div className="w-full sm:max-w-md bg-gradient-to-br from-[#0B1220] to-[#0F1A2E] border-t sm:border border-white/10 sm:rounded-3xl rounded-t-3xl shadow-2xl">
        <div className="p-5 pb-6 space-y-4">
          {/* Hero */}
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 shrink-0 rounded-2xl bg-gradient-to-br from-[#FF8A00] to-[#FF5C00] flex items-center justify-center shadow-[0_8px_24px_rgba(255,138,0,0.5)]">
              <ShieldCheck size={22} weight="fill" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black tracking-[0.25em] text-[#FF8A00]/85 uppercase">
                Bắt buộc · Bảo mật tài khoản
              </div>
              <h2 className="text-[19px] font-black text-white leading-tight mt-1">
                Xác thực Số điện thoại
              </h2>
              <p className="text-[12.5px] text-white/70 mt-1.5 leading-relaxed">
                Bạn vừa đăng nhập bằng Google. Vui lòng cung cấp & xác thực SĐT
                qua Zalo OTP để tiếp tục sử dụng PickLeague — giúp BTC liên hệ
                khi cần và bảo vệ tài khoản của bạn.
              </p>
            </div>
          </div>

          {/* Phone input */}
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-widest text-white/55 font-black mb-1.5">
              Số điện thoại (Zalo)
            </span>
            <input
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value.replace(/[^0-9+\s]/g, "").slice(0, 15));
                // Phase 85.A.47 — Clear warning khi user gõ phone mới.
                if (phoneTakenWarning) setPhoneTakenWarning(null);
              }}
              placeholder="VD: 0901234567"
              className={`w-full h-12 px-4 rounded-xl bg-[#0F1623] border text-white text-[16px] font-bold tabular-nums tracking-wider outline-none ${
                phoneTakenWarning
                  ? "border-rose-400/60 focus:border-rose-400"
                  : "border-white/15 focus:border-[#FF8A00]/60"
              }`}
              data-testid="phone-verify-required-input"
              disabled={saving}
            />
          </label>

          {/* Phase 85.A.47 — Inline warning: phone đã dùng. */}
          {phoneTakenWarning && (
            <div
              className="flex items-start gap-2 px-3 py-2.5 rounded-xl border border-rose-400/40 bg-rose-500/10 text-rose-100 text-[12.5px] leading-relaxed"
              data-testid="phone-verify-taken-warning"
            >
              <Warning size={16} weight="fill" className="text-rose-300 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">{phoneTakenWarning.message || "Số điện thoại đã được sử dụng. Vui lòng nhập số khác."}</div>
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              setPhoneTakenWarning(null);
              setShowOtp(true);
            }}
            disabled={!phoneValid || saving}
            className="w-full h-12 rounded-2xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] text-white text-[13px] font-black tracking-wide shadow-[0_8px_24px_rgba(255,138,0,0.45)] disabled:opacity-40 inline-flex items-center justify-center gap-2"
            data-testid="phone-verify-required-continue"
          >
            {saving ? "Đang lưu…" : "Tiếp tục với OTP Zalo"}
            <ArrowRight size={14} weight="bold" />
          </button>

          <div className="text-center pt-1">
            <button
              type="button"
              onClick={async () => {
                if (
                  window.confirm(
                    "Bạn sẽ được đăng xuất. Đăng nhập lại khi đã sẵn sàng xác thực SĐT?"
                  )
                ) {
                  await logout();
                  window.location.href = "/login";
                }
              }}
              className="text-[11.5px] text-white/40 hover:text-white/70 underline"
              data-testid="phone-verify-required-logout"
            >
              Đăng xuất, để xác thực sau
            </button>
          </div>

          <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3 text-[11px] text-white/55 leading-relaxed">
            Tài khoản:{" "}
            <span className="text-white/75 font-bold">{user?.email}</span>
            <br />
            Bạn cần xác thực 1 lần. Sau đó SĐT sẽ được khoá để bảo vệ tài khoản.
          </div>
        </div>
      </div>

      <PhoneOtpModal
        open={showOtp}
        phone={phoneDigits}
        onClose={() => setShowOtp(false)}
        onVerified={onVerified}
        onPhoneTaken={(detail) => setPhoneTakenWarning(detail)}
      />
    </div>
  );
}
